/* eslint-disable import/no-duplicates */
import {
  addDays as addDaysfns,
  addMonths as addMonthsfns,
  differenceInDays as differenceInDaysfns,
  differenceInMinutes,
  differenceInYears,
  format,
  formatISO,
  getMonth,
  getTime as getTimefns,
  getYear,
  isMatch,
  isValid,
  parse,
  parseISO,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subYears,
  toDate,
} from 'date-fns';
import { format as formatTZ, utcToZonedTime } from 'date-fns-tz';
import { enUS, es, fr } from 'date-fns/locale';

export const getLocale = () => {
  const locale = localStorage.getItem('language');
  switch (locale) {
    case 'enUS':
      return { locale: enUS };
    case 'fr':
      return { locale: fr };
    case 'es':
      return { locale: es };
    default:
      return { locale: enUS };
  }
};

export const formatDate = (date: Date, dateFormat: string = 'LLL dd, yyyy') => {
  if (isValid(date)) {
    return format(date, dateFormat || 'LLL dd, yyyy', getLocale());
  }

  return '';
};

export const formatTime = (date: Date, dateFormat: string = 'HH:mm') => {
  if (isValid(date)) {
    return format(date, dateFormat, getLocale());
  }

  return '';
};

export const isDateValid = (date: Date) => {
  return isValid(date);
};

export const formatTimeZoneDate = (
  utcDate: string,
  dataFormat: string,
  timeZone: string,
) => {
  if (!utcDate || !dataFormat || !timeZone) return '';

  const date = new Date(`${utcDate} UTC`);
  if (!isValid(date)) {
    return '';
  }
  const zonedDate = utcToZonedTime(date, timeZone);
  return formatTZ(zonedDate, dataFormat, { timeZone });
};

export const formatDateTime = (
  date: Date,
  dateFormat: string = 'LLL dd, yyyy HH:mm a',
) => {
  if (isValid(date)) {
    return format(date, dateFormat, getLocale());
  }

  return '';
};

export const addDays = (date: Date | number, days: number) =>
  addDaysfns(date, days);
export const addMonths = (date: Date | number, amount: number) =>
  addMonthsfns(date, amount);
export const differenceInDays = (
  startDate: Date | number,
  endDate: Date | number,
) => differenceInDaysfns(startDate, endDate);

export const getLastMonth = (date: Date = new Date()) => {
  const startDate = subMonths(date, 1);
  return [startDate, date];
};

export const getLastWeek = (date: Date = new Date()) => {
  const startDate = subDays(date, 7);
  return [startDate, date];
};

export const getLastFifteenMin = (date: Date = new Date()) => {
  const fifteenMinutesAgo = subMinutes(date, 15);
  return [fifteenMinutesAgo, date];
};

export const getLastOneHrs = (date: Date = new Date()) => {
  const oneHourAgo = subHours(date, 1);
  return [oneHourAgo, date];
};

export const getLastThreeHrs = (date: Date = new Date()) => {
  const oneHourAgo = subHours(date, 3);
  return [oneHourAgo, date];
};

export const getLastOneDay = (date: Date = new Date()) => {
  const twentyFourHoursAgo = subDays(date, 1);
  return [twentyFourHoursAgo, date];
};

export const getLastThreeDay = (date: Date = new Date()) => {
  const threeDaysAgo = subDays(date, 3);
  return [threeDaysAgo, date];
};

export const getShortMonth = (date: Date) => {
  if (!isValid(date)) {
    return formatTime(new Date(`${date}-10`), 'LLL');
  }

  return formatTime(date, 'LLL');
};

export const formatIso = (date: Date) => formatISO(date);
export const convertToDate = (date: any) => parseISO(date);

export const getDifferenceInMinutes = (
  laterDate: Date,
  earlierDate: Date,
  options?: {},
) => {
  if (isValid(laterDate) && isValid(earlierDate)) {
    return differenceInMinutes(laterDate, earlierDate, options);
  }
  return '';
};

export const formatDateRange = (dates: (Date | null)[], locale = 'enUS') => {
  if (
    !dates ||
    dates.length < 2 ||
    (dates[0] && dates[1] && dates[0] >= dates[1])
  ) {
    return '';
  }

  if (dates[0] && dates[1] && getYear(dates[0]) === getYear(dates[1])) {
    if (getMonth(dates[0]) === getMonth(dates[1])) {
      return `${format(dates[0], 'LLL dd', getLocale())} - ${format(
        dates[1],
        'dd, yyyy',
        getLocale(),
      )}`;
    }
    return `${format(dates[0], 'LLL dd', getLocale())} - ${format(
      dates[1],
      'LLL dd, yyyy',
      getLocale(),
    )}`;
  }
  return `${dates[0] ? format(dates[0], 'LLL dd, yyyy', getLocale()) : ''} - ${
    dates[1] ? format(dates[1], 'LLL dd, yyyy', getLocale()) : ''
  }`;
};

export const parseDate = (
  dateString: string,
  formatString: string = 'MM/dd/yyyy',
  referenceDate: number | Date = new Date(),
) => {
  return parse(dateString, formatString, referenceDate);
};

export const isValidDate = (
  dateString: string,
  formatString: string = 'MM/dd/yyyy',
) => {
  const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
  if (!dateRegex.test(dateString)) {
    return false;
  }
  return isMatch(dateString, formatString);
};

export const convertDateToString = (date: Date | number) => {
  return getTimefns(date).toString();
};

export const convertMilliSecondToDate = (millisecond: string | {} | null) => {
  return toDate(Number(millisecond));
};

export const formatDateString = (date: string, dateFormat: string) => {
  try {
    return format(convertToDate(date), dateFormat, getLocale());
  } catch (e) {
    return '';
  }
};

export const getISO8601TimezoneOffset = (date: Date) => {
  const offset = date.getTimezoneOffset();
  const absoluteOffset = Math.abs(offset);
  const hours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0');
  const minutes = String(absoluteOffset % 60).padStart(2, '0');
  const sign = offset <= 0 ? '+' : '-';
  return `${sign}${hours}:${minutes}`;
};

export const convertTimeStringTo24Hour = (time: string) => {
  // Split the time into parts (e.g., '02:30 PM' -> ['02', '30', 'PM'])
  const [timePart, modifier]: string[] = time.split(' ');
  const [hours, minutes]: string[] = timePart.split(':');

  // Convert hours to a number
  let hoursNum: number = parseInt(hours, 10);

  // Adjust for PM or AM
  if (modifier === 'PM' && hoursNum !== 12) {
    hoursNum += 12;
  } else if (modifier === 'AM' && hoursNum === 12) {
    hoursNum = 0; // Midnight case
  }

  // Pad hours and minutes with leading zero if necessary
  const hoursStr: string = hoursNum < 10 ? `0${hoursNum}` : hoursNum.toString();
  const minutesStr: string = minutes.length === 1 ? `0${minutes}` : minutes;

  return [hoursStr, minutesStr];
};

export const validateInputHours = (hoursInput: string) => {
  if (!hoursInput) {
    return false;
  }
  // Convert input to a number
  const hours = Number(hoursInput);
  // Check if it's a valid number and within the range 1-12
  return !Number.isNaN(hours) && hours >= 1 && hours <= 12;
};

export const validateInputMinutes = (minuteInput: string) => {
  if (minuteInput === null || minuteInput === '' || minuteInput === undefined) {
    return false;
  }
  // Convert input to a number
  const minutes = Number(minuteInput);
  // Check if it's a valid number and within the range 00-59
  return !Number.isNaN(minutes) && minutes >= 0 && minutes <= 59;
};

export const formatDateTimeRange = (dates: (Date | null)[]) => {
  if (
    !dates ||
    dates.length < 2 ||
    (dates[0] && dates[1] && dates[0] >= dates[1])
  ) {
    return '';
  }

  return `${dates[0] ? format(dates[0], 'M/d, h:mm a', getLocale()) : ''} - ${
    dates[1] ? format(dates[1], 'M/d, h:mm a', getLocale()) : ''
  }`;
};

export const getISO8601TimezoneOffSetFromTimeZone = (timezone: string) => {
  const timeFormat: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'longOffset',
    timeZone: timezone,
  };
  const str = new Date().toLocaleString('en', timeFormat);
  const offset = ` (GMT${str.split('GMT')[1]})`;
  return offset;
};

export const getUTCOffset = (timeZone: string) => {
  if (!timeZone) {
    return '';
  }
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    hour12: false,
    timeZoneName: 'shortOffset',
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);

  const offsetPart = parts.find((p) => p.type === 'timeZoneName');
  return offsetPart?.value || '';
};

export const convertToUTCStartOfDay = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00Z`;
};

export const convertToUTCEndOfDay = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T23:59:59Z`;
};

export const getDifferenceInYearsAndDaysString = (
  startDate: string | Date,
  endDate: string | Date,
) => {
  // Ensure dates are in Date format
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;

  // Calculate full years difference
  const years = differenceInYears(end, start);

  // Calculate remaining days after removing full years
  const remainingDays = differenceInDays(subYears(end, years), start);

  return { years, remainingDays };
};
